import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import type { RootState } from '@src/store/store';
import {
    logout,
    setNewToken,
    setUserPermissions,
} from '@src/store/user/userSlice';
import { Result } from './api.types';
import { authService } from '../auth/authService';

const mutex = new Mutex();
const permissionsMutex = new Mutex();

// Create base query with auth header support
const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
        const state = (getState as () => RootState)();
        const token = state.user.user?.accessToken;

        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }

        return headers;
    },
});

// Helper function to refresh permissions after API calls
const refreshPermissions = async (api: any, baseQueryFn: typeof baseQuery) => {
    // Skip if already refreshing permissions
    if (permissionsMutex.isLocked()) {
        return;
    }

    const release = await permissionsMutex.acquire();
    try {
        const state = api.getState() as RootState;
        const token = state.user.user?.accessToken;

        // Only refresh if user is authenticated
        if (!token) {
            return;
        }

        // Call permissions endpoint directly using baseQuery (not baseQueryWithReauth to avoid recursion)
        // Handle 401 by trying to refresh token first
        let permissionsResult = await baseQueryFn(
            {
                url: '/users/my-permissions',
                method: 'GET',
                headers: {
                    'Accept-Language': 'en',
                },
            },
            api,
            {}
        );

        // If 401, try to refresh token and retry
        if (permissionsResult.error?.status === 401 && !mutex.isLocked()) {
            const tokenRelease = await mutex.acquire();
            try {
                const newToken = await authService.getTokenSilently();
                if (newToken) {
                    api.dispatch(setNewToken(newToken));
                    // Retry permissions call with new token
                    permissionsResult = await baseQueryFn(
                        {
                            url: '/users/my-permissions',
                            method: 'GET',
                            headers: {
                                'Accept-Language': 'en',
                            },
                        },
                        api,
                        {}
                    );
                }
            } catch (err) {
                // Silently fail
            } finally {
                tokenRelease();
            }
        }

        if (permissionsResult.data) {
            const permissionsResponse = permissionsResult.data as any;
            if (permissionsResponse?.data?.permissions) {
                api.dispatch(
                    setUserPermissions(permissionsResponse.data.permissions)
                );
            }
        }
    } catch (error) {
        // Silently fail - don't break other API calls if permissions refresh fails
        console.warn('Failed to refresh permissions:', error);
    } finally {
        release();
    }
};

// Enhanced base query with auto-refresh and 401 handling
const baseQueryWithReauth = async (
    args: any,
    api: any,
    extraOptions: any
): Promise<Result> => {
    // Wait for any ongoing token refresh
    await mutex.waitForUnlock();

    // Check if this is the permissions endpoint to avoid infinite loop
    const isPermissionsEndpoint = args?.url === '/users/my-permissions';

    let result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                const newToken = await authService.getTokenSilently();
                if (newToken) {
                    api.dispatch(setNewToken(newToken));
                    // retry the initial query
                    result = await baseQuery(args, api, extraOptions);
                } else {
                    api.dispatch(logout(undefined));
                }
            } catch (err) {
                api.dispatch(logout(undefined));
            } finally {
                // release must be called once the mutex should be released again.
                release();
            }
        } else {
            // wait until the mutex is available without locking it
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }

    // Refresh permissions after successful API calls (except for permissions endpoint itself)
    if (!isPermissionsEndpoint && !result.error) {
        // Refresh permissions asynchronously to not block the current request
        refreshPermissions(api, baseQuery).catch(() => {
            // Silently handle errors
        });
    }

    return result as Result;
};

export const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'plans',
        'sims',
        'zones',
        'countries',
        'operators',
        'subAccounts',
        'simInstances',
        'users',
        'roles',
        'signalingEvents',
        'contacts',
        'addresses',
        'logs',
        'permissions',
        'profile',
        'discountCodes',
        'sales',
        'supportTickets',
        'tenantCountries',
        'tenantRegions',
    ],
    endpoints: () => ({}),
});
